import removeUndefinedKeys from 'root-core/utils/object/removeUndefinedKeys.js'
import sum from 'root-core/utils/array/sum.js'
import values from 'lowscore/values.js'
import sortBy from 'lowscore/sortBy.js'
import debugFactory from 'debug'
const debug = debugFactory('booking-services:price-engine')
import LocalDate from '@booking-services/localDate'
import roundMoney from './roundMoney.js'
import * as priceTravelProtection from './priceTravelProtection.js'
import priceTransfers from './priceTransfers.js'
import * as pricePromotions from './pricePromotions.js'
import * as priceEPDs from './priceEPDs.js'
import priceExtraStays from './priceExtraStays.js'
import * as priceRooms from './priceRooms.js'
import priceAdditionalProducts from './priceAdditionalProducts.js'
import PerPassengerPrices from './perPassengerPrices.js'
import discountCombinability from './discountCombinability.js'
import * as priceFTD from './priceFrequentTravellerDiscount.js'
import mandatoryProducts from './mandatoryProducts.js'
import paymentSchedules from '../paymentSchedules/index.js'
import ValidationError from '../../services/web/server/public-api/validationError.js'

function PriceEngine (options) {
  this.departure = options.departure
  this.insuranceName = options.insuranceName || 'Travel Protection'
  this.paymentSchedulePolicies = options.paymentSchedulePolicies
  this.canFetchPaymentSchedules = options.canFetchPaymentSchedules
}

PriceEngine.prototype.price = function (
  priceOptions,
  additionalData = {},
  bookingDate = new LocalDate()
) {
  try {
    debug('priceOptions %o', priceOptions)

    const prices = this.departure.prices
    priceOptions.discounts = priceOptions.discounts || {}

    prices.adjustments.discounts.availableFrequentTravellerDiscount =
      priceFTD.applicableFrequentTravellerDiscount(
        this.departure,
        bookingDate
      )

    const cheapestBreakdown = findCheapestBreakdownForCombinabilityRuleSet(
      bookingDate,
      {
        departure: this.departure,
        insuranceName: this.insuranceName,
        priceOptions: priceOptions,
        requestedRooms: priceOptions.rooms,
        promotions: additionalData.promotions,
        flights: additionalData.flights,
        promisedFlights: additionalData.promisedFlights,
        transfers: additionalData.transfers,
        prices: prices,
        perPassengerPrices: new PerPassengerPrices(prices),
        applicableFrequentTravellerDiscounts: prices.adjustments.discounts.availableFrequentTravellerDiscount,
        bookingDate: bookingDate,
        features: additionalData.features,
        passengers: additionalData.passengers,
      }
    )

    if (additionalData.flights) {
      cheapestBreakdown.totalWithoutFlights = roundMoney(
        cheapestBreakdown.total - cheapestBreakdown.flights.total
      )
    }

    if (additionalData.promisedFlights) {
      cheapestBreakdown.totalWithoutFlights = roundMoney(
        cheapestBreakdown.total - cheapestBreakdown.promisedFlights.total
      )
    }

    cheapestBreakdown.totalBeforeDiscount = roundMoney(
      cheapestBreakdown.total +
        values(cheapestBreakdown.discounts).reduce((sum, d) => sum + d.amount, 0)
    )
    cheapestBreakdown.hasDiscounts =
      cheapestBreakdown.total != cheapestBreakdown.totalBeforeDiscount
    cheapestBreakdown.aggregatedMiscellaneousProducts =
      mandatoryProducts(cheapestBreakdown,this.insuranceName).aggregatedMiscellaneousProducts

    cheapestBreakdown.productsExcludedFromDiscountsPrice = cheapestBreakdown.requestedAdditionalProducts.reduce((additionalProductPrice, product) => {
      if(['prepaidGratuities', 'travelProtection'].includes(product.productType)) {
        return additionalProductPrice + product.amount
      }
      return additionalProductPrice
    }, 0)

    cheapestBreakdown.operatingCompanyTax = this.departure.operatingCompanyTax
    debug('breakdown %o', cheapestBreakdown)

    if (this.paymentSchedulePolicies && this.canFetchPaymentSchedules) {
      try {
        cheapestBreakdown.paymentSchedule = paymentSchedules({
          departure: this.departure,
          paymentSchedulePolicies: this.paymentSchedulePolicies,
          priceBreakdown: cheapestBreakdown,
        })
      } catch (err) {
        debug('payment schedules', err)
      }
    }

    return cheapestBreakdown
  }
  catch (err) {
    if (err instanceof ValidationError) {
      return err
    }
    throw err
  }
}

function findCheapestBreakdownForCombinabilityRuleSet (
  bookingDate,
  priceBreakdownOptions
) {
  const breakdowns = priceForEachCombinabilityRuleSet(
    bookingDate,
    priceBreakdownOptions
  )
  return sortBy(breakdowns, b => b.breakdown.total)[0].breakdown
}

function priceForEachCombinabilityRuleSet (
  bookingDate,
  priceBreakdownOptions
) {
  var combinabilityRules = discountCombinability.effectiveCombinabilityRules({
    rules: priceBreakdownOptions.prices.adjustments.discounts.combinabilityRules,
    bookingDate,
    party: priceBreakdownOptions.priceOptions.party,
  })
  return combinabilityRules.map(rule => {
    return {
      breakdown: priceBreakdownForCombinabilityRule(
        rule,
        priceBreakdownOptions
      ),
      rule: rule,
    }
  })
}

function priceBreakdownForCombinabilityRule (combinabilityRule, options) {
  const departure = options.departure
  const priceOptions = options.priceOptions
  const prices = options.prices
  const flights = options.flights
  const promisedFlights = options.promisedFlights
  const discounts = {}
  const requestedPromotions = (options.promotions || []).slice(0)

  const roomsWithGroupDiscount = priceRooms.roomsWithBestCombinationOfDiscounts(
    combinabilityRule,
    options
  )

  const rooms = roomsWithGroupDiscount.rooms

  rooms.forEach(room => {
    room.passengers.forEach(passenger => {
      passenger.roomType = room.roomType
    })
  })

  const passengers = rooms.flatMap(room => room.passengers)

  passengers.forEach((passenger, i) => {
    passenger.passengerId = passenger.passengerId || i + 1
  })

  if (roomsWithGroupDiscount.groupDiscount) {
    const amountPerPassenger = passengers.map(p => {
      return {
        passengerId: p.passengerId,
        amount: roomsWithGroupDiscount.groupDiscount.amount / passengers.length,
      }
    })
    discounts.groupDiscount = Object.assign({}, roomsWithGroupDiscount.groupDiscount, {amountPerPassenger})
  }
  const transfers = priceTransfers(options.transfers, passengers)
  const extraStays = priceExtraStays(priceOptions, options.departure, options.passengers, options.features)
  discounts.frequentTravellerDiscount = priceFTD.price(passengers)

  const epd = priceEPDs.bestAvailableEPD(
    prices.adjustments.discounts.epds,
    passengers,
    options.bookingDate,
    options.departure
  )

  if (epd && priceOptions.discounts.epd) {
    requestedPromotions.push(epd)
  }

  const promotionsNotIncludingTravelProtection = pricePromotions.breakdownPromotionsWithoutTravelProtection({
    promotions: requestedPromotions,
    passengers,
    departure,
    transfers,
    flights,
    promisedFlights,
    extraStays,
    bookingDate: options.bookingDate,
  })

  const extrasSubTotal = calculateExtrasSubTotal({
    extraStays,
    transfers,
    flights,
    promisedFlights,
    groupDiscount: discounts.groupDiscount,
  })

  priceTravelProtection.applyTravelProtectionToPassengers({
    options,
    passengers,
    promotions: promotionsNotIncludingTravelProtection,
    extraAmountPerPassenger: extrasSubTotal / passengers.length,
  })

  discounts.promotions = pricePromotions.promotionsIncludingTravelProtection(
    options,
    passengers,
    promotionsNotIncludingTravelProtection
  )
  var promoSubTotal = discounts.promotions ? discounts.promotions.amount : 0

  removeUndefinedKeys(discounts)

  const passengersSubTotal = priceRooms.calculateRoomsSubtotal(rooms)
  const total = passengersSubTotal + extrasSubTotal - promoSubTotal
  var roundedTotal = roundMoney(total)

  return {
    total: roundedTotal < 0 ? 0 : roundedTotal,
    currency: prices.currency,
    rooms,
    passengers,
    extraStays,
    transfers,
    flights,
    promisedFlights,
    discounts,
    requestedAdditionalProducts: priceAdditionalProducts(
      rooms,
      options.insuranceName,
      options.departure,
      priceOptions
    ),
    availableEpd: epd,
  }
}

function calculateExtrasSubTotal ({
  extraStays,
  transfers,
  flights,
  promisedFlights,
  groupDiscount,
}) {
  const extraStayBeforeAmount = extraStays.before ? extraStays.before.total : 0
  const extraStayAfterAmount = extraStays.after ? extraStays.after.total : 0
  const transfersAmount = transfers
    ? sum(transfers.map(transfer => transfer.total))
    : 0
  const flightsAmount = flights ? flights.total : 0
  const promisedFlightsAmount = promisedFlights ? promisedFlights.total : 0
  const groupDiscountAmount = groupDiscount ? groupDiscount.amount : 0

  return (
    extraStayBeforeAmount +
    extraStayAfterAmount +
    flightsAmount +
    transfersAmount +
    promisedFlightsAmount -
    groupDiscountAmount
  )
}

PriceEngine.prototype.productNotAddedForRequestedPromotion = (
  promotion,
  passengers,
  transfers,
  extraStays,
  departure
) => {
  return !pricePromotions.productForPromoAddedToOrder({
    promotion,
    passengers,
    transfers,
    extraStays,
    departure,
  })
}

PriceEngine.prototype.promotionsNotCombinableWith = (
  promotions,
  promotion,
  appliedEpd
) => {
  let promosToCompare = promotions.slice(0)
  if (appliedEpd) {
    promosToCompare.push(appliedEpd)
  }
  return pricePromotions.notCombinableWith(promosToCompare, promotion)
}

PriceEngine.prototype.pricePromotion = function (
  promotion,
  passengers,
  additionalData = {},
  bookingDate = new LocalDate()
) {
  return (
    pricePromotions.pricePromotion({
      promotion,
      passengers,
      departure: this.departure,
      transfers: additionalData.transfers,
      flights: additionalData.flights,
      extraStays: additionalData.extraStays,
      bookingDate,
    }) ||
    pricePromotions.breakdownTravelProtectionPromotion(
      {
        promotions: [promotion],
        departure: this.departure,
        bookingDate,
      },
      passengers
    ) || {amount: 0}
  )
}

export default function (options) {
  return new PriceEngine(options)
}
